// Dictating to the Brain (plan brain-multimodal, D7, R11).
//
// Press the microphone, speak, press it again: what was said lands in the
// input box, **not sent**. Whoever dictates reads it and fixes it first -- that
// is the difference from passing a voice note through the paperclip.
//
// It records with the browser and transcribes through the same `media/enrich/`
// a voice note uses, so there is one transcription and one model to choose
// (`gpt-4o-mini-transcribe` since 2026-09-28). What it cost waits here until
// the next message goes out, and travels with it (R12).
//
// The browser only hands out the microphone on `https` or `localhost`.

import { useCallback, useRef, useState } from 'react'
import { describirAdjunto, type Adjunto } from '@/services/brainApi'

/** What `media/enrich/` wraps a transcription in, which a dictation does not
 *  want: its heading, and a `[m:ss]` mark per segment when there are any. */
export function plainTranscript(text: string): string {
  return text
    .split('\n')
    .filter((line) => !line.trim().startsWith('#'))
    .map((line) => line.replace(/^\s*\[\d+:\d{2}(?::\d{2})?\]\s*/, ''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function useDictation({
  onText,
  onError,
  agenteId,
}: {
  onText: (text: string) => void
  onError: (reason: 'microphone' | 'transcription') => void
  agenteId?: string
}) {
  const [recording, setRecording] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  // The live microphone, while recording: the wave is drawn from it, so the
  // person sees it is listening (asked for on 2026-09-28).
  const [stream, setStream] = useState<MediaStream | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const cost = useRef<Adjunto | null>(null)

  const start = useCallback(async () => {
    let micro: MediaStream
    try {
      micro = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      onError('microphone')
      return
    }
    const chunks: Blob[] = []
    const rec = new MediaRecorder(micro)
    rec.ondataavailable = (event) => chunks.push(event.data)
    rec.onstop = async () => {
      micro.getTracks().forEach((track) => track.stop())
      setStream(null)
      const mime = (rec.mimeType || 'audio/webm').split(';')[0]
      const file = new File(chunks, `dictado.${mime.split('/')[1] || 'webm'}`, { type: mime })
      setTranscribing(true)
      try {
        const heard = await describirAdjunto(file, { type: 'audio', mime }, agenteId)
        cost.current = heard.adjunto
        const text = plainTranscript(heard.texto)
        if (text) onText(text)
      } catch {
        onError('transcription')
      } finally {
        setTranscribing(false)
      }
    }
    recorder.current = rec
    rec.start()
    setStream(micro)
    setRecording(true)
  }, [agenteId, onError, onText])

  const toggle = useCallback(() => {
    if (recorder.current && recording) {
      recorder.current.stop()
      recorder.current = null
      setRecording(false)
      return
    }
    void start()
  }, [recording, start])

  /** What transcribing cost, once: the message that goes out carries it. */
  const takeCost = useCallback(() => {
    const pending = cost.current
    cost.current = null
    return pending
  }, [])

  return { recording, transcribing, stream, toggle, takeCost }
}
